// src/utils/services/hedisAiService.js
import { callOpenAIChat } from './openaiClient';

// Helper: extrae el JSON aunque venga con ```json ... ``` o texto extra
const safeParseJsonFromModel = (rawContent) => {
  if (!rawContent) {
    throw new Error('No content to parse');
  }

  let text = Array.isArray(rawContent)
    ? rawContent
        .map((p) =>
          typeof p === 'string'
            ? p
            : (p && (p.text || p.content || '')) || ''
        )
        .join('')
    : String(rawContent);

  text = text.trim();

  // Limpiar bloques de código markdown
  if (text.startsWith('```')) {
    text = text.replace(/^```[a-zA-Z]*\s*/m, '');
    text = text.replace(/```$/m, '');
    text = text.trim();
  }

  // Recortar desde el primer '{' al último '}'
  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    console.error('[safeParseJsonFromModel] No JSON object found in text:', text);
    throw new Error('No JSON object found in model response');
  }

  const jsonSlice = text.slice(firstBrace, lastBrace + 1);

  return JSON.parse(jsonSlice);
};

export const generateHedisMetricsFromAI = async ({ hedContextText }) => {
  const contextText = hedContextText || '';

  if (!contextText.trim()) {
    console.warn('[generateHedisMetricsFromAI] Empty HEDIS context text');
    return [];
  }

  const systemPrompt = `You are a physician and quality-measures specialist focused on HEDIS (Healthcare Effectiveness Data and Information Set).

CRITICAL RULES:
- You MUST base your evaluation EXCLUSIVELY on the patient-specific clinical data provided below.
- Do NOT invent, assume, or fabricate any clinical findings, dates, lab values, or diagnoses not present in the context.
- If a data element is missing (e.g., no labs, no vitals, no notes), mark the measure as "Open" and state what data is missing in the recommendation.
- Use the EXACT dates, values, and readings from the provided context — never guess approximate dates.
- Each patient has different conditions — only evaluate measures relevant to THIS patient's documented diagnoses and demographics.
- If no clinical notes or data are provided for a patient, return an empty metrics array rather than generic measures.`;

  const userPrompt = `Evaluate HEDIS-like quality measures for the patient described below.

For each applicable measure, determine:
- Whether the requirement is CLOSED (compliant) or OPEN (action needed) based on the actual clinical data.
- The specific clinical evidence that supports your determination (exact date, exact value).
- What action is needed to close any gaps.

Return ONLY a JSON object (no extra text):

{
  "metrics": [
    {
      "id": "measure_code",
      "measure": "Full measure name",
      "domain": "Clinical domain category",
      "applicable": true,
      "status": "Open",
      "reason": "Specific explanation referencing actual patient data (dates, values, findings from the notes)",
      "lastDate": "YYYY-MM-DD from the actual data or 'N/A'",
      "nextDue": "YYYY-MM-DD or 'N/A'",
      "value": "The actual clinical value found in data (e.g. 'BP 142/88', 'A1c 8.1%') or 'N/A' if not found",
      "target": "Target threshold (e.g. '< 140/90', '< 8.0%') or 'N/A'",
      "gapRecommendation": "Specific actionable recommendation to close the gap, referencing what is missing or outdated"
    }
  ]
}

Standard candidate measures to evaluate WHEN the patient has the corresponding condition:
- CBP: Controlling High Blood Pressure (if hypertension diagnosis or elevated BP in notes)
- HBD: Hemoglobin A1c Control (if diabetes diagnosis)
- KED: Kidney Health Evaluation for Diabetes (if diabetes diagnosis)
- SPC: Statin Therapy for Cardiovascular Disease (if CVD/ASCVD)
- SPR: Spirometry Testing in COPD (if COPD diagnosis)
- CDC: Comprehensive Diabetes Care (if diabetes)
- OMW: Osteoporosis Management in Women (if applicable by age/sex)
- COL: Colorectal Cancer Screening (age-appropriate)
- BCS: Breast Cancer Screening (age/sex-appropriate)

Rules:
- Only include measures that are APPLICABLE based on the patient's documented conditions and demographics.
- Mark "NotApplicable" for measures where the patient clearly does not have the condition.
- You may add other reasonable measures if CLEARLY supported by documented diagnoses.
- Do NOT add measures based on assumed conditions — only use explicitly documented diagnoses.

===== PATIENT CLINICAL CONTEXT =====

${contextText}`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ],
    temperature: 0.15,
  });

  const content = data?.choices?.[0]?.message?.content;
  console.log('[generateHedisMetricsFromAI] RAW content from OpenAI:', content);

  if (!content) {
    console.error('[generateHedisMetricsFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJsonFromModel(content);

    if (!json.metrics || !Array.isArray(json.metrics)) {
      console.error('[generateHedisMetricsFromAI] Unexpected JSON format:', json);
      throw new Error('Unexpected JSON format (missing "metrics")');
    }

    // Normalizar y filtrar: solo métricas aplicables
    return json.metrics
      .filter((m) => m.applicable !== false && m.status !== 'NotApplicable')
      .map((m) => ({
        id: m.id || '',
        measure: m.measure || '',
        domain: m.domain || '',
        applicable: Boolean(m.applicable),
        status: m.status || 'NotApplicable',
        reason: m.reason || '',
        lastDate: m.lastDate || 'N/A',
        nextDue: m.nextDue || 'N/A',
        value: m.value || '',
        target: m.target || '',
        gapRecommendation: m.gapRecommendation || '',
      }));
  } catch (err) {
    console.error('[generateHedisMetricsFromAI] Error parsing JSON:', err);
    return [];
  }
};
